/**
 * A client as a writer (PLAN §6.1): its HLC clock, origin id, `ord` writer tag, Yjs client id, row ids and
 * clientOpIds. One implementation for the `acp` CLI (one writer per process) and the SPA (one per editor session),
 * which each had a near-identical copy (review 2026-09-27 §2.2); they differ only in their prefixes.
 *
 * **One writer per process or session.** HLCs must stay unique per writer (D6): two `acp` invocations can run at once
 * from one machine, and a user may have a rundown open in two tabs — sharing a node id they could mint the same HLC in
 * the same millisecond. A fresh random node id per writer makes that impossible without any locking. Causality is kept
 * by `observe`-ing every row the writer has seen before stamping, so a new write is newer than what it read even when
 * the wall clock stepped back.
 */
import { createHlcClock, type HlcClock } from '@norna/domain/hlc.js';
import { writerTagFor } from '@norna/domain/ordering.js';
import type { Row } from '@norna/domain/row.js';
import { createUlidFactory } from '@norna/domain/ulid.js';

export type Writer = {
  readonly nodeId: string;
  readonly clock: HlcClock;
  /** The `ord` suffix for this writer (`ordering.ts`). */
  readonly ordTag: string;
  /**
   * The Yjs client id text edits are written under (a uint32). A Yjs client numbers its insertions, and each text edit
   * is built from the text state the writer holds; if an edit is rolled back after the server may have applied it (a
   * request that failed without an answer), the next edit would reuse those numbers for different characters, which
   * Yjs cannot tell apart — so after any unanswered or refused text edit the SPA `rotateTextClient`s.
   */
  readonly textClientId: () => number;
  readonly rotateTextClient: () => void;
  readonly newId: () => string;
  readonly newOpId: () => string;
  /** Fold every HLC on `row` into the clock (null: nothing seen). */
  readonly observe: (row: Row | null) => void;
};

/** Who the writer is: `cli` / `acp` for the CLI, `web` / `web` for the SPA (node id `<node>-…`, clientOpId `<op>-<ulid>`). */
export type WriterPrefixes = { readonly node: string; readonly op: string };

const ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyz';

export function createWriter(prefixes: WriterPrefixes, nowMs: () => number, randomBytes: (count: number) => Uint8Array): Writer {
  const suffix = Array.from(randomBytes(10), (b) => ALPHABET.charAt(b % ALPHABET.length)).join('');
  const nodeId = `${prefixes.node}-${suffix}`;
  const clock = createHlcClock(nodeId, nowMs, null);
  const newId = createUlidFactory(nowMs, randomBytes);
  // A Yjs client id is a uint32.
  const newClientId = (): number => new DataView(Uint8Array.from(randomBytes(4)).buffer).getUint32(0);
  let textClientId = newClientId();
  return {
    nodeId,
    clock,
    ordTag: writerTagFor(nodeId),
    textClientId: () => textClientId,
    rotateTextClient: () => {
      textClientId = newClientId();
    },
    newId,
    newOpId: () => `${prefixes.op}-${newId()}`,
    observe: (row) => {
      if (row === null) return;
      for (const hlc of Object.values(row.h)) clock.receive(hlc);
    },
  };
}
